/**
 * Menu Dialogs — modal dialogs triggered by menu items
 * Reuses the .modal-overlay / .modal-dialog pattern from projectSettingsDialog.ts
 */

// ── Reusable confirm dialog ──────────────────────────────────

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
}

export function showConfirmDialog(opts: ConfirmOptions): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-dialog confirm-dialog">
      <div class="modal-header">
        <span class="modal-title">${opts.title}</span>
        <button class="modal-close">&times;</button>
      </div>
      <div class="modal-body confirm-body">
        <p style="margin:0;font-size:11px;color:var(--text-secondary);">${opts.message}</p>
      </div>
      <div class="confirm-footer">
        <button class="confirm-btn confirm-cancel">Cancel</button>
        <button class="confirm-btn confirm-ok">${opts.confirmLabel || 'OK'}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.querySelector('.modal-close')!.addEventListener('click', close);
  overlay.querySelector('.confirm-cancel')!.addEventListener('click', close);
  overlay.querySelector('.confirm-ok')!.addEventListener('click', () => {
    close();
    opts.onConfirm();
  });
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) close();
  });
}

// ── Helper: simple info dialog ───────────────────────────────

function showInfoDialog(title: string, bodyHtml: string): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-dialog info-dialog">
      <div class="modal-header">
        <span class="modal-title">${title}</span>
        <button class="modal-close">&times;</button>
      </div>
      <div class="modal-body info-body">${bodyHtml}</div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector('.modal-close')!.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) close();
  });
}

// ── Helper: form dialog with OK/Cancel ───────────────────────

function showFormDialog(title: string, bodyHtml: string, onOk?: (form: HTMLElement) => void): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-dialog settings-dialog">
      <div class="modal-header">
        <span class="modal-title">${title}</span>
        <button class="modal-close">&times;</button>
      </div>
      <div class="modal-body settings-form">${bodyHtml}</div>
      <div class="settings-footer">
        <button class="confirm-btn confirm-cancel">Cancel</button>
        <button class="confirm-btn confirm-ok">OK</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector('.modal-close')!.addEventListener('click', close);
  overlay.querySelector('.confirm-cancel')!.addEventListener('click', close);
  overlay.querySelector('.confirm-ok')!.addEventListener('click', () => {
    if (onOk) onOk(overlay.querySelector('.settings-form')!);
    close();
  });
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) close();
  });
}

// ── File Menu Dialogs ────────────────────────────────────────

export function showGenerateCacheReport(): void {
  showInfoDialog('Cache Report', `
    <div class="info-field"><span class="info-field-label">Cache Items:</span> <span class="info-field-value">0</span></div>
    <div class="info-field"><span class="info-field-label">Cache Size:</span> <span class="info-field-value">0 MB</span></div>
    <div class="info-field"><span class="info-field-label">Cache Location:</span> <span class="info-field-value">C:\\ProgramData\\NewBlueFX\\Cache</span></div>
    <p style="margin:10px 0 0;font-size:10px;color:var(--text-muted);">No cached items found. Cache is generated during render operations.</p>
  `);
}

// ── Settings Dialogs ─────────────────────────────────────────

export function showAudioMonitorDialog(): void {
  showFormDialog('Audio Monitor', `
    <div class="settings-row">
      <label class="settings-label">Output Device:</label>
      <select class="settings-select">
        <option>Default System Device</option>
        <option>Speakers (Realtek Audio)</option>
        <option>HDMI Audio Output</option>
      </select>
    </div>
    <div class="settings-row">
      <label class="settings-label">Volume:</label>
      <input type="range" min="0" max="100" value="80" class="settings-input" style="flex:1;">
      <span style="font-size:10px;color:var(--text-secondary);min-width:30px;text-align:right;">80%</span>
    </div>
  `);
}

export function showBlackmagicAtemDialog(): void {
  showFormDialog('Blackmagic Design ATEM Connection', `
    <div class="settings-row">
      <label class="settings-label">IP Address:</label>
      <input type="text" class="settings-input" value="192.168.1.100" style="flex:1;">
    </div>
    <div class="settings-row">
      <label class="settings-label">Status:</label>
      <span style="font-size:10px;color:#e04040;">Not Connected</span>
    </div>
    <div style="margin-top:8px;">
      <button class="confirm-btn confirm-ok" style="font-size:10px;" id="atem-connect-btn">Connect</button>
    </div>
    <p style="margin:8px 0 0;font-size:10px;color:var(--text-muted);">Simulation mode — ATEM connections are not available.</p>
  `);
}

export function showInternetAccountsDialog(): void {
  showFormDialog('Internet Accounts', `
    <div style="border:1px solid #333;border-radius:2px;min-height:80px;background:#1a1a1a;padding:8px;margin-bottom:8px;">
      <p style="margin:0;font-size:10px;color:var(--text-muted);text-align:center;padding:20px 0;">No accounts configured</p>
    </div>
    <div class="settings-row">
      <label class="settings-label">Type:</label>
      <select class="settings-select">
        <option>YouTube Live</option>
        <option>Facebook Live</option>
        <option>Twitch</option>
        <option>Custom RTMP</option>
      </select>
    </div>
    <div class="settings-row">
      <label class="settings-label">Stream Key:</label>
      <input type="password" class="settings-input" placeholder="Enter stream key..." style="flex:1;">
    </div>
  `);
}

// ── Conferencing Dialogs ─────────────────────────────────────

export function showZoomJoinDialog(): void {
  showFormDialog('Join Zoom Meeting', `
    <div class="settings-row">
      <label class="settings-label">Meeting ID:</label>
      <input type="text" class="settings-input" placeholder="Enter meeting ID" style="flex:1;">
    </div>
    <div class="settings-row">
      <label class="settings-label">Password:</label>
      <input type="password" class="settings-input" placeholder="Meeting password" style="flex:1;">
    </div>
    <div class="settings-row">
      <label class="settings-label">Display Name:</label>
      <input type="text" class="settings-input" value="NEVCO Pro Video" style="flex:1;">
    </div>
    <p style="margin:8px 0 0;font-size:10px;color:var(--text-muted);">Simulation mode — Zoom integration is not available.</p>
  `, () => {
    // Simulated: do nothing
  });
}

export function showTeamsJoinDialog(): void {
  showFormDialog('Join Microsoft Teams Meeting', `
    <div class="settings-row">
      <label class="settings-label">Meeting Link:</label>
      <input type="text" class="settings-input" placeholder="Paste Teams meeting link..." style="flex:1;">
    </div>
    <div class="settings-row">
      <label class="settings-label">Display Name:</label>
      <input type="text" class="settings-input" value="NEVCO Pro Video" style="flex:1;">
    </div>
    <p style="margin:8px 0 0;font-size:10px;color:var(--text-muted);">Simulation mode — Teams integration is not available.</p>
  `, () => {
    // Simulated: do nothing
  });
}

export function showTeamsNdiHelp(): void {
  showInfoDialog('Microsoft Teams NDI Configuration', `
    <p style="font-size:11px;color:var(--text-secondary);line-height:1.5;">
      To use NDI with Microsoft Teams:
    </p>
    <ol style="font-size:10px;color:var(--text-secondary);line-height:1.6;padding-left:18px;margin:8px 0;">
      <li>Open Microsoft Teams Settings</li>
      <li>Navigate to <b>App permissions</b></li>
      <li>Enable <b>Network Device Interface (NDI)</b></li>
      <li>Join a meeting and enable NDI broadcast from the meeting toolbar</li>
      <li>In NEVCO Pro Video, the Teams NDI source will appear as an available input</li>
    </ol>
    <p style="font-size:10px;color:var(--text-muted);margin-top:8px;">
      Note: NDI for Teams requires a Teams desktop client and NDI Runtime installed on the system.
    </p>
  `);
}

// ── Program Out Setup Dialogs ────────────────────────────────

export function showWatchFolderSetup(): void {
  showFormDialog('Watch Folder Setup', `
    <div class="settings-row">
      <label class="settings-label">Output Folder:</label>
      <input type="text" class="settings-input" value="C:\\Output\\WatchFolder" style="flex:1;" readonly>
    </div>
    <div class="settings-row">
      <label class="settings-label">Format:</label>
      <select class="settings-select">
        <option>PNG</option>
        <option>JPEG</option>
        <option>BMP</option>
        <option>TIFF</option>
      </select>
    </div>
    <p style="margin:8px 0 0;font-size:10px;color:var(--text-muted);">Output frames will be written to the specified folder for external consumption.</p>
  `);
}

export function showPngSequenceSetup(): void {
  showFormDialog('PNG Sequence Setup', `
    <div class="settings-row">
      <label class="settings-label">Output Folder:</label>
      <input type="text" class="settings-input" value="C:\\Output\\PNGSequence" style="flex:1;" readonly>
    </div>
    <div class="settings-row">
      <label class="settings-label">File Prefix:</label>
      <input type="text" class="settings-input" value="frame_" style="flex:1;">
    </div>
    <div class="settings-row">
      <label class="settings-label">Start Number:</label>
      <input type="number" class="settings-input" value="0" min="0" style="width:80px;">
    </div>
    <p style="margin:8px 0 0;font-size:10px;color:var(--text-muted);">Sequential PNG frames will be numbered starting from the specified start number.</p>
  `);
}

// ── Help Dialogs ─────────────────────────────────────────────

export function showGettingStarted(): void {
  showInfoDialog('Getting Started with NEVCO Pro Video', `
    <p style="font-size:11px;color:var(--text-secondary);margin-bottom:10px;">
      Welcome to NEVCO Pro Video — professional broadcast graphics software for live production.
    </p>
    <p style="font-size:10px;color:var(--text-secondary);margin-bottom:6px;font-weight:600;">Basic Workflow:</p>
    <ul style="font-size:10px;color:var(--text-secondary);line-height:1.6;padding-left:18px;margin:0 0 10px;">
      <li>Browse templates in the <b>Graphics Library</b></li>
      <li>Drag templates to the <b>Project</b> panel to add them</li>
      <li>Edit text and data in the <b>Properties</b> panel</li>
      <li>Preview your changes in the <b>Preview</b> monitor</li>
      <li>Use the <b>Play</b> button or Take to send graphics to Program output</li>
      <li>Assign graphics to zones for multi-layer composition</li>
    </ul>
    <p style="font-size:10px;color:var(--text-muted);">Press F1 at any time to open this help. Use Help &gt; Shortcuts for keyboard reference.</p>
  `);
}

export function showManageActivation(): void {
  showInfoDialog('Manage Activation', `
    <div class="info-field"><span class="info-field-label">Product:</span> <span class="info-field-value">NEVCO Pro Video</span></div>
    <div class="info-field"><span class="info-field-label">Status:</span> <span class="info-field-value" style="color:#2a9f45;">Activated (Simulation)</span></div>
    <div class="info-field"><span class="info-field-label">Serial:</span> <span class="info-field-value">SIM-XXXX-XXXX-XXXX</span></div>
    <div class="info-field"><span class="info-field-label">Licensed To:</span> <span class="info-field-value">BroadcastSIMS Student</span></div>
    <p style="margin:10px 0 0;font-size:10px;color:var(--text-muted);">This is a simulation environment. No actual license is required.</p>
  `);
}

export function showAboutNdi(): void {
  showInfoDialog('About NDI', `
    <p style="font-size:11px;color:var(--text-secondary);line-height:1.5;">
      <b>NDI</b> (Network Device Interface) is a royalty-free software specification developed by
      NewTek that enables video-compatible products to communicate, deliver, and receive high-definition
      video over a computer network in a high-quality, low-latency manner.
    </p>
    <div class="info-field" style="margin-top:10px;"><span class="info-field-label">NDI Version:</span> <span class="info-field-value">5.6.1</span></div>
    <div class="info-field"><span class="info-field-label">Runtime:</span> <span class="info-field-value">Simulated</span></div>
    <p style="margin:8px 0 0;font-size:10px;color:var(--text-muted);">NDI&reg; is a registered trademark of Vizrt NDI AB.</p>
  `);
}

export function showApiDocumentation(): void {
  showInfoDialog('API Documentation', `
    <p style="font-size:11px;color:var(--text-secondary);line-height:1.5;margin-bottom:10px;">
      NEVCO Pro Video exposes a REST API and WebSocket interface for external control and automation.
    </p>
    <p style="font-size:10px;color:var(--text-secondary);font-weight:600;">Available Endpoints:</p>
    <ul style="font-size:10px;color:var(--text-secondary);line-height:1.6;padding-left:18px;margin:4px 0 10px;">
      <li><code style="color:var(--accent-blue);">GET /api/v1/project</code> — Get project state</li>
      <li><code style="color:var(--accent-blue);">POST /api/v1/take</code> — Execute take transition</li>
      <li><code style="color:var(--accent-blue);">PUT /api/v1/fields/:id</code> — Update field values</li>
      <li><code style="color:var(--accent-blue);">WS /ws</code> — Real-time event stream</li>
    </ul>
    <p style="margin:0;font-size:10px;color:var(--text-muted);">API access is available in the production version of the software.</p>
  `);
}

export function showAnalytics(): void {
  showInfoDialog('Analytics', `
    <p style="font-size:10px;color:var(--text-secondary);margin-bottom:8px;">Usage Statistics (Simulation Session)</p>
    <table style="width:100%;border-collapse:collapse;font-size:10px;">
      <tr style="border-bottom:1px solid #333;">
        <td style="padding:4px 8px;color:var(--text-muted);">Templates Loaded</td>
        <td style="padding:4px 8px;color:var(--text-secondary);text-align:right;">4</td>
      </tr>
      <tr style="border-bottom:1px solid #333;">
        <td style="padding:4px 8px;color:var(--text-muted);">Takes Executed</td>
        <td style="padding:4px 8px;color:var(--text-secondary);text-align:right;">0</td>
      </tr>
      <tr style="border-bottom:1px solid #333;">
        <td style="padding:4px 8px;color:var(--text-muted);">Field Updates</td>
        <td style="padding:4px 8px;color:var(--text-secondary);text-align:right;">0</td>
      </tr>
      <tr style="border-bottom:1px solid #333;">
        <td style="padding:4px 8px;color:var(--text-muted);">Session Duration</td>
        <td style="padding:4px 8px;color:var(--text-secondary);text-align:right;">—</td>
      </tr>
      <tr>
        <td style="padding:4px 8px;color:var(--text-muted);">Render Errors</td>
        <td style="padding:4px 8px;color:var(--text-secondary);text-align:right;">0</td>
      </tr>
    </table>
  `);
}

export function showCreateSupportPackage(): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-dialog info-dialog">
      <div class="modal-header">
        <span class="modal-title">Create Support Package</span>
        <button class="modal-close">&times;</button>
      </div>
      <div class="modal-body info-body">
        <p style="font-size:11px;color:var(--text-secondary);margin-bottom:10px;" id="support-status">Collecting system information...</p>
        <div style="background:#1a1a1a;border:1px solid #333;border-radius:2px;height:6px;overflow:hidden;">
          <div id="support-progress" style="height:100%;background:var(--accent-blue);width:0%;transition:width 0.3s;"></div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector('.modal-close')!.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) close();
  });

  const progress = overlay.querySelector('#support-progress') as HTMLElement;
  const status = overlay.querySelector('#support-status') as HTMLElement;
  let pct = 0;
  const timer = setInterval(() => {
    pct += 20;
    progress.style.width = `${Math.min(pct, 100)}%`;
    if (pct >= 40) status.textContent = 'Collecting logs...';
    if (pct >= 80) status.textContent = 'Packaging...';
    if (pct >= 100) {
      clearInterval(timer);
      status.textContent = 'Support package created successfully.';
      status.style.color = '#2a9f45';
    }
  }, 400);
}

export function showAboutCaptivate(): void {
  showInfoDialog('About NEVCO Pro Video', `
    <div style="text-align:center;padding:8px 0;">
      <p style="font-size:14px;font-weight:700;color:var(--text-primary);margin:0 0 4px;">NEVCO Pro Video</p>
      <p style="font-size:10px;color:var(--text-muted);margin:0 0 4px;">Powered by Titler Live 5</p>
      <p style="font-size:10px;color:var(--text-secondary);margin:0 0 8px;">Version 5.0 Build 230101 (Simulation)</p>
      <hr style="border:none;border-top:1px solid #333;margin:8px 0;">
      <p style="font-size:9px;color:var(--text-muted);margin:0;">&copy; 2024 NewBlue, Inc. All rights reserved.</p>
      <p style="font-size:9px;color:var(--text-muted);margin:4px 0 0;">Simulation by BroadcastSIMS for educational use only.</p>
    </div>
  `);
}

// ── View Dialogs ─────────────────────────────────────────────

export function showMessageLog(): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const timestamp = new Date().toLocaleTimeString();
  overlay.innerHTML = `
    <div class="modal-dialog settings-dialog" style="min-width:480px;">
      <div class="modal-header">
        <span class="modal-title">Message Log</span>
        <button class="modal-close">&times;</button>
      </div>
      <div class="modal-body msglog-body">
        <textarea class="msglog-textarea" readonly>[${timestamp}] Application started
[${timestamp}] Graphics Library loaded — 4 templates registered
[${timestamp}] Zone system initialized — preset: Full Screen
[${timestamp}] Preview monitor ready
[${timestamp}] Program output ready
[${timestamp}] Keyboard shortcuts registered
[${timestamp}] Panel docking system initialized
[${timestamp}] Simulation ready</textarea>
      </div>
      <div class="msglog-footer">
        <button class="confirm-btn confirm-cancel" id="msglog-clear">Clear</button>
        <button class="confirm-btn confirm-ok" id="msglog-close">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector('.modal-close')!.addEventListener('click', close);
  overlay.querySelector('#msglog-close')!.addEventListener('click', close);
  overlay.querySelector('#msglog-clear')!.addEventListener('click', () => {
    const ta = overlay.querySelector('.msglog-textarea') as HTMLTextAreaElement;
    ta.value = '';
  });
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) close();
  });
}
